import { GetListResponse, HttpError, useDeleteMany, useList, useUpdate } from '@refinedev/core';
import { QueryObserverResult } from '@tanstack/react-query';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';

export type LifeEvent = {
  id: string;
  description: string;
  cost: number;
  details: string;
  eventDate: string;
  clientId: string;
};

type UseLifeEventsReturnType = QueryObserverResult<GetListResponse<LifeEvent>, HttpError> & {
  lifeEventsData: LifeEvent[] | undefined;
  upsertLifeEvents: (items: LifeEvent[]) => Promise<void>;
  deleteLifeEvents: (ids: string[]) => Promise<void>;
};

export default function useLifeEvents(): UseLifeEventsReturnType {
  const { clientId } = useCurrentClient();

  const result = useList<LifeEvent>({
    resource: `clients/${clientId}/life-events`,
  });

  const { mutateAsync: upsertLifeEventsMutation } = useUpdate<LifeEvent[]>({
    resource: `clients/${clientId}/life-events`,
    id: 'batch',
  });

  const upsertLifeEvents = async (items: LifeEvent[]) => {
    await upsertLifeEventsMutation({
      values: items,
    });

    await result.refetch();
  };

  const { mutateAsync: deleteLifeEventsMutation } = useDeleteMany({});
  const deleteLifeEvents = async (ids: string[]) => {
    await deleteLifeEventsMutation({
      resource: `clients/${clientId}/life-events`,
      ids,
    });
  };

  return {
    lifeEventsData: result.data?.data,
    upsertLifeEvents,
    deleteLifeEvents,
    ...result,
  };
}
